import React from 'react';
import { generatePath } from 'react-router-dom';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import EmployeeLink from '@/components/common/EmployeeLink';
import RouterLink from '@/components/common/RouterLink';
import ShowHideToggleIcon from '@/components/common/ShowHideToggleIcon';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import intl from '@/lib/intl/intl';
import translate from '@/lib/intl/translate';
import EmailParticipantTypes from '../constants/EmailParticipantTypes';
import { EmailParticipantPropType } from '../types';

const MAX_PARTICIPANTS_FOR_SHOW_MORE = 10;
const SHOW_HIDE_TOGGLE_ICON_PROPS = {
  fontSize: 'small',
};

/**
 * There is some "wonkiness" with the layout of the <ShowHideToggle> combined
 * with its sibling inline elements. If you see some weird looking styles here,
 * it is to deal with that wonkiness. The SVG has something to do with it so
 * relying upon absolute position for it to play nice. The vertical-align and the
 * negative margin-top allow for moving <ShowHideToggle> up a few pixels, allowing
 * for it to align up with it's sibling.
 */
const SHOW_HIDE_TOGGLE_CLASSES = {
  root: 'align-middle mt-[-2px] h-[22px] inline-flex items-center flex-row-reverse type-caption1 text-text-secondary relative',
  icon: 'absolute top[-2px] right-0',
  text: 'mr-5',
};

export default function EmailParticipants({ participants, showAll }) {
  const numOfParticipants = participants?.length || 1;
  const [isShowingMore, setIsShowingMore] = React.useState(() => {
    return showAll;
  });
  const handleShowHideToggle = () => setIsShowingMore((prev) => !prev);

  const renderedParticipants = Array.isArray(participants)
    ? participants.slice(
        0,
        isShowingMore ? participants.length : MAX_PARTICIPANTS_FOR_SHOW_MORE,
      )
    : [participants];
  const formatListOptions = {
    type: isShowingMore ? 'conjunction' : 'unit',
  };

  return (
    <>
      {intl.formatList(
        renderedParticipants.map((participant, i) => {
          const className =
            !showAll && i + 1 === renderedParticipants.length ? 'mr-2' : '';

          if (participant.resourceType === EmailParticipantTypes.CONSTITUENT) {
            const { constituentId, displayName, emailAddress } = participant;

            return (
              <Tooltip
                data-testid={`constituent-${constituentId}-email-tooltip`}
                key={constituentId}
                placement="bottom"
                title={emailAddress.toLowerCase()}
              >
                <span className={className}>
                  <RouterLink
                    data-testid={`constituent-${constituentId}-participant-link`}
                    to={generatePath(AuthRoutes.CONSTITUENT, {
                      constituentId,
                    })}
                  >
                    {displayName}
                  </RouterLink>
                </span>
              </Tooltip>
            );
          }

          if (participant.resourceType === EmailParticipantTypes.EMPLOYEE) {
            const { userId, displayName } = participant;

            return (
              <EmployeeLink
                key={userId}
                classes={{ root: className }}
                data-testid={`employee-${userId}-participant-link`}
                displayName={displayName}
                userId={userId}
              />
            );
          }

          const emailAddress = participant.emailAddress.toLowerCase();

          return (
            <Link
              key={emailAddress}
              className={className}
              data-testid={`unknown-${emailAddress}-participant-link`}
              href={`mailto:${emailAddress}`}
            >
              {emailAddress}
            </Link>
          );
        }),
        formatListOptions,
      )}
      {!showAll && numOfParticipants > MAX_PARTICIPANTS_FOR_SHOW_MORE && (
        <button
          className="inline-flex"
          data-testid="show-hide-email-participants"
          onClick={handleShowHideToggle}
          type="button"
        >
          <ShowHideToggleIcon
            IconProps={SHOW_HIDE_TOGGLE_ICON_PROPS}
            classes={SHOW_HIDE_TOGGLE_CLASSES}
            isOpen={isShowingMore}
            hideText={translate('HIDE_MORE')}
            showText={translate('SHOW_X_MORE', {
              num: numOfParticipants - MAX_PARTICIPANTS_FOR_SHOW_MORE,
            })}
          />
        </button>
      )}
    </>
  );
}

EmailParticipants.propTypes = {
  participants: PropTypes.oneOfType([
    PropTypes.arrayOf(EmailParticipantPropType),
    EmailParticipantPropType,
  ]).isRequired,
  showAll: PropTypes.bool,
};

EmailParticipants.defaultProps = {
  showAll: false,
};
